import * as React from 'react';
import * as Styled from './Reviews.styles';
import ReviewItem from './Review';
import { useProductReviews } from './useProductReviews';
import Center from '@ids/components/Layout/Center';
import CTAButton, { useCTAButtonTracking } from '@ids/components/CTAButton';
import { useIDSSku, useReviewsBlock } from '@ids/context';
import useInView from '@ids/hooks/useInView';
import { Maybe } from '@ids/types/utility';
import useIdsGaTracking from '@ids/hooks/useIdsGaTracking';
import Divider from '@ids/components/Layout/Divider';

interface ProductReviewsProps {
    sku: string;
    inView: boolean;
}

const ProductReviews = ({ sku, inView }: ProductReviewsProps) => {
    const reviewsBlock = useReviewsBlock();
    const { trackIdsEvent } = useIdsGaTracking();
    const { productReviews, showAllProductReviews, showLoadMoreButton } = useProductReviews(
        sku,
        reviewsBlock?.initialReviews,
        inView
    );

    const ariaAttributes = {};

    if (!productReviews?.length) {
        ariaAttributes['aria-busy'] = true;
    }

    const handleLoadMoreReviewsClick = () => {
        showAllProductReviews();
        trackIdsEvent({
            action: 'Content interaction click',
            label: 'see more reviews',
        });
    };

    return (
        <React.Fragment>
            <Styled.Reviews data-testid="reviews" {...ariaAttributes}>
                {!productReviews?.length ? (
                    <React.Fragment>
                        <Styled.ReviewSkeletonItem data-testid="review_item_skeleteon" />
                        <Styled.ReviewSkeletonItem data-testid="review_item_skeleteon" />
                        <Styled.ReviewSkeletonItem data-testid="review_item_skeleteon" />
                        <Styled.ReviewSkeletonItem data-testid="review_item_skeleteon" />
                    </React.Fragment>
                ) : (
                    productReviews.map(review => <ReviewItem key={`${review.title}-${review.date}`} review={review} />)
                )}
            </Styled.Reviews>
            {showLoadMoreButton && (
                <Styled.ReviewLoadMore onClick={handleLoadMoreReviewsClick}>
                    {reviewsBlock?.readMoreText}
                </Styled.ReviewLoadMore>
            )}
        </React.Fragment>
    );
};

const Reviews = () => {
    const reviewsBlock = useReviewsBlock();
    const sku = useIDSSku();
    const trackCTAButtonClick = useCTAButtonTracking();

    const containerRef = React.useRef<Maybe<HTMLDivElement>>(null);
    const isElementInView = useInView<HTMLDivElement>({
        containerRef,
        options: { rootMargin: '100px' },
    });

    if (!reviewsBlock || !sku) {
        return null;
    }

    return (
        <React.Fragment>
            <Styled.ReviewsSection data-testid="reviews_block_section" ref={containerRef} id="reviews">
                <Styled.Heading>{reviewsBlock?.title}</Styled.Heading>
                <ProductReviews sku={sku} inView={isElementInView} />
                <Center>
                    <CTAButton data-testid="reviews-cta-button" onClick={() => trackCTAButtonClick('ga_reviews_cta')}>
                        {reviewsBlock?.ctaText}
                    </CTAButton>
                </Center>
            </Styled.ReviewsSection>
            <Divider />
        </React.Fragment>
    );
};

export default Reviews;
